import {
    PAYEE_CATALOG_TYPE_DEFAULT,
    PAYEE_CATALOG_TYPE_EVENT,
    PAYEE_CATALOG_TYPE_ORDERING,
    PAYEE_CATALOG_TYPE_BOOKING,
    PAYEE_CATALOG_TYPE_SCHEDULING,
    PAYEE_CATALOG_TYPE_DELIVERY,
    PAYEE_CATALOG_TYPE_DELIVERY_API_RESPONSE_CODES,
} from "../constants/payeeCatalog"


import {
    PAYEE_ORDER_STATUS_CREATED,
    PAYEE_ORDER_STATUS_PAID,
    PAYEE_ORDER_STATUS_SEEN,
    PAYEE_ORDER_STATUS_READY_FOR_PICKUP,
    PAYEE_ORDER_STATUS_PICKED_UP,
    PAYEE_ORDER_STATUS_COMPLETED,
    PAYEE_ORDER_STATUS_CANCELLED,
    FULFILLMENT_METHOD_DELIVERY,
} from "../constants/payeeOrder"
import { getNextOpeningStatusMessage } from "./payeeUtils"
import { capitalizeAllWords } from "./stringUtils"
import { calculateTransactionFees } from "./feeUtils";
import { getCartTotal } from "./cartUtils"
import { addDurations } from "./datetimeUtils"
import currency from 'currency.js'
/**
 * Get the list of available statuses for the order, based on both the statuses for that catalog type and the current status
 * 
 * @param {string} catalogType The type of catalog that the order was made from
 * @param {string} currentStatus The current status of the order
 */
export const getPayeeOrderStatusOptions = (catalogType, currentStatus) => {
    //if the order is cancelled, the status cannot be changed again
    if (currentStatus === PAYEE_ORDER_STATUS_CANCELLED) return [PAYEE_ORDER_STATUS_CANCELLED]
    //choose the possible order statuses based on the catalog type
    const defaultOrderStatuses = [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_COMPLETED]
    const orderStatusesByCatalogType = {
        [PAYEE_CATALOG_TYPE_DEFAULT]: defaultOrderStatuses,
        [PAYEE_CATALOG_TYPE_ORDERING]: [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_READY_FOR_PICKUP, PAYEE_ORDER_STATUS_COMPLETED],
        [PAYEE_CATALOG_TYPE_DELIVERY]: [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_READY_FOR_PICKUP, PAYEE_ORDER_STATUS_PICKED_UP, PAYEE_ORDER_STATUS_COMPLETED],
        [PAYEE_CATALOG_TYPE_EVENT]: [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_COMPLETED],
        [PAYEE_CATALOG_TYPE_SCHEDULING]: [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_COMPLETED],
        [PAYEE_CATALOG_TYPE_BOOKING]: [PAYEE_ORDER_STATUS_CREATED, PAYEE_ORDER_STATUS_PAID, PAYEE_ORDER_STATUS_SEEN, PAYEE_ORDER_STATUS_COMPLETED],
    }
    let statuses = orderStatusesByCatalogType[catalogType] ? 
                     orderStatusesByCatalogType[catalogType]
                     :
                     defaultOrderStatuses
    //filter the possible statuses by the current status, to get the currently available statuses 
    const currentStatusIndex = statuses.findIndex(s => s === currentStatus)
    //if the current status is not provided or found, return the full list of possible statuses
    if (!currentStatus || typeof currentStatus !== "string" || currentStatusIndex === -1) return statuses
    else {
        statuses = statuses.slice(
            currentStatusIndex, 
            currentStatusIndex + 2 //show the next available status, but not the one after that
        )
    }
    return statuses
}

export const addWaitTimes = (waitTimeA, waitTimeB) => {
    if (typeof waitTimeA !== "object" || typeof waitTimeB !== "object") return waitTimeA
    const {avg:avgA="00:00", buffer:bufferA="00:00"} = waitTimeA || {}
    const {avg:avgB="00:00", buffer:bufferB="00:00"} = waitTimeB || {}
    const combinedAvgTime = addDurations(avgA, avgB)
    const combinedBufferTime = addDurations(bufferA, bufferB)
    return {
        avg: combinedAvgTime,
        buffer: combinedBufferTime
    }
}

/**
 * Purpose: extract the catalog metadata to append to the payee order. A specialized metadata object will be made for each type of order
 */
export const getPayeeOrderCatalogMetadata = ({payeeCatalog, cart}) => {
    const metadata = {}
    if (!payeeCatalog || typeof payeeCatalog !== "object") return metadata
    const {catalogType, config} = payeeCatalog
    if (!config || typeof config !== "object") return metadata
    //handle ordering catalogs
    if (catalogType === PAYEE_CATALOG_TYPE_ORDERING && config[catalogType]) {
        //add the prep times to the metadata
        const {prepTimes} = config[catalogType]
        if (prepTimes) {
            metadata.prepTimes = prepTimes
            metadata.waitTimes = prepTimes //default to only the prep times, to handle pickup
        }
        const {catalogMetadata:cartCatalogMetadata={}} = cart
        const {fulfillmentMethod="", pickupAddress=null, deliveryAddress=null, deliveryAvailability=null, deliveryCatalogId=""} = cartCatalogMetadata
        metadata.fulfillmentMethod = fulfillmentMethod
        metadata.pickupAddress = pickupAddress
        if (fulfillmentMethod === FULFILLMENT_METHOD_DELIVERY) {
            metadata.deliveryAddress = deliveryAddress
            metadata.deliveryAvailability = deliveryAvailability
            metadata.deliveryCatalogId = deliveryCatalogId
            if (deliveryAvailability && deliveryAvailability.deliveryTimes){
                metadata.waitTimes = addWaitTimes(prepTimes, deliveryAvailability.deliveryTimes)
            }
        }
    }
    return metadata
}

export const validateOrderingPayeeOrder = ({cart={}, catalogType=""}) => {
    let errors = []
    if (catalogType !== PAYEE_CATALOG_TYPE_ORDERING) return errors 
    const {catalogMetadata={}} = cart || {}
    const {fulfillmentMethod="", deliveryAddress, deliveryAvailability, pickupAddress={}} = catalogMetadata || {}
    if (!pickupAddress || !pickupAddress.settlementId) errors.push(`No pickup address specified`)
    //if the fulfillment method is pickup, we skip most checks 
    //otherwise, check these delivery-specific things
    if (fulfillmentMethod === FULFILLMENT_METHOD_DELIVERY) {
        //check for a valid address
        if (!deliveryAddress || !deliveryAddress.settlementId) errors.push(`Please tell us where to deliver your items`)
        //check that delivery is avaiable
        if (!deliveryAvailability) errors.push(`Delivery availability has not been loaded. Please wait`)
        else if (typeof deliveryAvailability === "object" && !deliveryAvailability.available) {
            const {code:errorCode="", openingTimes:deliveryOpeningTimes={}} = deliveryAvailability
            let errorMessage = `Delivery is unavailable`
            const deliverySettlementName = deliveryAddress.city
            const pickupSettlementName = pickupAddress.city || ""
            switch (errorCode) {
                case PAYEE_CATALOG_TYPE_DELIVERY_API_RESPONSE_CODES.OUT_OF_DELIVERY_RANGE: {
                    errorMessage =`Cannot deliver to ${capitalizeAllWords(deliverySettlementName)}. Try an area nearer to ${capitalizeAllWords(pickupSettlementName)}`; 
                    break;
                }
                case PAYEE_CATALOG_TYPE_DELIVERY_API_RESPONSE_CODES.OUT_OF_PICKUP_RANGE: {
                    errorMessage =`Cannot pick up from ${capitalizeAllWords(pickupSettlementName)}`;
                    break;
                }
                case PAYEE_CATALOG_TYPE_DELIVERY_API_RESPONSE_CODES.CLOSED: {
                    const {isOpen, nextOpeningTime, nextClosingTime} = deliveryOpeningTimes || {}
                    errorMessage = `Delivery service is closed, however you can still pick up. ${getNextOpeningStatusMessage(isOpen, nextOpeningTime, nextClosingTime)}`
                    break;
                }
                case PAYEE_CATALOG_TYPE_DELIVERY_API_RESPONSE_CODES.INACTIVE: {
                    errorMessage =`Delivery service is not active`;
                    break;
                }
                default: errorMessage = "Delivery unavailable"
            }
            errors.push(errorMessage)
        }
        //check for detailed directions
        if (deliveryAddress && (!deliveryAddress.directions || String(deliveryAddress.directions).length < 3)) errors.push(`Please provide a street address or directions`) 
    }       
    return errors
}

export const getPayeeOrderFeesList = (cart={}, catalogType="", payeeSenderFee=0) => {
    //if there is no cart, return no fees
    if (!cart || typeof cart !== "object") return []
    const feeList = []
    const {catalogMetadata={}, lineItemsById={}} = cart 
    const itemSubTotalXcd = Object.values(lineItemsById).reduce((sum, i) => {
        const itemTotal = currency(i.price).multiply(i.quantity)
        return currency(sum).add(itemTotal).value
    }, 0)
    //calculate the processing fee
    const {senderFeeXcd:merchantProcessingFeeXcd=0} = calculateTransactionFees(itemSubTotalXcd, {senderFee: payeeSenderFee})
    let processingFeeXcd = merchantProcessingFeeXcd
    //handle ordering catalogs
    if (catalogType === PAYEE_CATALOG_TYPE_ORDERING){
        const {fulfillmentMethod="", deliveryAvailability=null} = catalogMetadata
        if (fulfillmentMethod === FULFILLMENT_METHOD_DELIVERY && deliveryAvailability){
            const {price: deliveryPrice=0, priceBreakdown={}} = deliveryAvailability
            if (deliveryPrice) feeList.push({total: deliveryPrice, label: "Delivery"})
            //add delivery processing fee to any existing processing fee
            processingFeeXcd = currency(processingFeeXcd).add(priceBreakdown.senderFeeXcd).value
        }
    }
    //add the final processing fee to the fee list, last
    if (processingFeeXcd > 0) feeList.push({total: processingFeeXcd, label: "Processing"}) 
    return feeList
}

export const getPayeeOrderTotal = (cart, catalogType="", payeeSenderFee=0) => {
    //if there is no cart, return no fees
    if (!cart || typeof cart !== "object") return {orderTotalXcd: 0, subTotalXcd: 0}
    const itemSubTotalXcd = getCartTotal(cart)
    let subTotalXcd = itemSubTotalXcd
    const {catalogMetadata={}} = cart 
    //calculate the processing fee
    const merchantProcessingFeeXcd = currency(itemSubTotalXcd).multiply(payeeSenderFee).value
    let orderTotalXcd = currency(itemSubTotalXcd).add(merchantProcessingFeeXcd).value
    //handle ordering catalogs
    if (catalogType === PAYEE_CATALOG_TYPE_ORDERING){
        const {fulfillmentMethod="", deliveryAvailability=null} = catalogMetadata
        if (fulfillmentMethod === FULFILLMENT_METHOD_DELIVERY && deliveryAvailability){
            const {price: deliveryPrice, pricePlusFeesXcd: deliveryPricePlusFeesXcd=0} = deliveryAvailability || {}
            //add delivery processing fee to any existing total
            orderTotalXcd = currency(orderTotalXcd).add(deliveryPricePlusFeesXcd).value
            subTotalXcd = currency(subTotalXcd).add(deliveryPrice).value
        }
    }
    return {orderTotalXcd, subTotalXcd}
}